import type { SpaceProvider } from "@/api/spaces/add.ts";
import type { operations } from "@/openapi.gen.ts";
import type { SpaceType } from "@/utils/space-reference-category.ts";
import { $api } from "@/utils/fetch.ts";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";

/**
 * Re-exported from `@/shared/` so the existing UI imports keep working. The
 * ceilings moved there because `POST /api/projects/{projectId}/links` now
 * enforces them too — previously they lived here and were therefore advisory:
 * every check was client-side, against whichever list that client held.
 */
export { MAX_LINKED_FILES, MAX_LINKED_SPACES } from "@/shared/project-links.ts";

export type ProjectTagColor =
	| "blue"
	| "green"
	| "red"
	| "orange"
	| "purple"
	| "gray";

export const PROJECT_TAG_COLORS: ProjectTagColor[] = [
	"blue",
	"green",
	"red",
	"orange",
	"purple",
	"gray",
];

/** Tailwind classes per tag color. Light + dark mode handled by the theme. */
export const PROJECT_TAG_COLOR_CLASSES: Record<ProjectTagColor, string> = {
	blue: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800",
	green:
		"bg-green-100 text-green-700 border-green-300 dark:bg-green-950 dark:text-green-300 dark:border-green-800",
	red: "bg-red-100 text-red-700 border-red-300 dark:bg-red-950 dark:text-red-300 dark:border-red-800",
	orange:
		"bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-800",
	purple:
		"bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800",
	gray: "bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-700",
};

export interface ProjectTag {
	id: string;
	name: string;
	color: ProjectTagColor;
}

export interface ProjectReferenceItem {
	id: string;
	name: string;
	spaceId?: string;
	/** Provider of the (owning) space. `oneData` marks a dataset reference. */
	provider?: SpaceProvider;
	/**
	 * Type of the (owning) space. `integration` marks a tool connection.
	 * Classify with `spaceReferenceCategory()` rather than reading this directly —
	 * a dataset is keyed on `provider`, not on this field.
	 */
	type?: SpaceType;
	/** Whether the referenced space is active (C47). Inactive references render as paused. */
	isActive?: boolean;
}

export interface SpaceLink {
	foreignSpaceId: string;
	foreignSpaceName: string;
	foreignSpaceProvider: SpaceProvider;
	foreignSpaceType: SpaceType;
	foreignFileId?: string;
	foreignSpaceIsActive: boolean;
}

export interface Project {
	id: string;
	name: string;
	description?: string;
	favorite: boolean;
	archived: boolean;
	tags: ProjectTag[];
	chatCount: number;
	createdAt: string;
	updatedAt: string;
	createdByName?: string;
}

export interface ProjectWithInstructions extends Project {
	systemPrompt: string;
	canWrite: boolean;
	isProjectAdmin: boolean;
	/** Members see only their own chats; project admins see all of them. */
	privateChats: boolean;
	spaceLinks: SpaceLink[];
	fileReferences: ProjectReferenceItem[];
	spaceReferences: ProjectReferenceItem[];
	/**
	 * The caller's OWN tool links — connector-package connections whose tools they
	 * linked into this project. Personal and independent of `spaceReferences`,
	 * which may link the same connection's corpus.
	 */
	toolLinkReferences: ProjectReferenceItem[];
}

export type ProjectSort = "name" | "createdAt" | "updatedAt";
export type ProjectArchiveFilter = "exclude" | "only" | "all";
export type ProjectFavoriteFilter = "exclude" | "only" | "all";
/** Whether a project needs one of the filtered tags (`any`) or every one (`all`). */
export type ProjectTagMatch = "any" | "all";

/**
 * Page size for the project lists that render together on /home.
 *
 * The sidebar nav and the start-chat picker are both on screen at once, and
 * `pageSize` is part of the query key — so different values meant two
 * `/api/projects` requests for the same data on every /home load. One constant
 * keeps them on one cache entry.
 *
 * 50 rather than 20 because the pickers sharing it do not paginate. The sidebar
 * only reads the personal project from it (always row 0); its favorites come
 * from their own `favorite: "only"` query.
 *
 * Deliberately not applied to the paginated lists (projects-overview-list and
 * friends), where the page size sets infinite-scroll granularity and 20 is a
 * real UX choice.
 */
export const PROJECT_LIST_PAGE_SIZE = 50;

export const useProjectsInfiniteQuery = ({
	search = "",
	pageSize = 10,
	sort,
	sortDir,
	archived,
	favorite,
	tagIds,
	tagMatch,
	enabled = true,
	keepPrevious = false,
}: {
	search?: string;
	pageSize?: number;
	sort?: ProjectSort;
	sortDir?: "asc" | "desc";
	archived?: ProjectArchiveFilter;
	favorite?: ProjectFavoriteFilter;
	tagIds?: string[];
	tagMatch?: ProjectTagMatch;
	/**
	 * Opt-out for a caller that is mounted before it is visible — the global
	 * search dialog is rendered by the sidebar on every page and would otherwise
	 * fetch a page of projects on every app load, for a palette nobody opened.
	 */
	enabled?: boolean;
	/**
	 * Keep the previous page on screen while a new query key resolves, instead of
	 * dropping to `isLoading` and whatever skeleton the caller renders. Opt-in
	 * rather than the default because it changes what every existing list shows
	 * mid-search, which is out of scope here.
	 */
	keepPrevious?: boolean;
}) => {
	return $api.useInfiniteQuery(
		"get",
		"/api/projects",
		{
			params: {
				query: {
					pageSize,
					search,
					sort,
					sortDir,
					archived,
					favorite,
					tagIds: tagIds && tagIds.length > 0 ? tagIds.join(",") : undefined,
					tagMatch,
				},
			},
		},
		{
			pageParamName: "page",
			initialPageParam: 0,
			enabled,
			placeholderData: keepPrevious ? keepPreviousData : undefined,
			getNextPageParam: (lastPage, allPages, lastPageParam) => {
				const loadedCount = allPages.reduce(
					(sum, page) => sum + (page.projects?.length || 0),
					0,
				);
				if (
					loadedCount < lastPage.totalCount &&
					typeof lastPageParam === "number"
				) {
					return lastPageParam + 1;
				}
				return undefined;
			},
		},
	);
};

export const projectsStatsQueryOptions = () =>
	$api.queryOptions("get", "/api/projects/stats");

export const projectQueryOptions = (projectId: string) =>
	$api.queryOptions(
		"get",
		"/api/projects/{projectId}",
		{ params: { path: { projectId } } },
		{
			select: (project) => {
				const spaceLinks = project.spaceLinks ?? [];

				// Derive file/space references from spaceLinks
				const fileReferences: ProjectReferenceItem[] = spaceLinks
					.filter((link) => link.foreignFileId)
					.map((link) => ({
						id: link.foreignFileId as string,
						name: link.foreignFileId as string,
						spaceId: link.foreignSpaceId,
						provider: link.foreignSpaceProvider,
						type: link.foreignSpaceType,
						isActive: link.foreignSpaceIsActive,
					}));

				// Datasets AND tool connections stay inside spaceReferences (they are
				// whole-space links) — `spaceReferenceCategory()` splits them into
				// their three categories at render time. Deliberately NOT filtered
				// here: `handleUpdateReferences` diffs the dialog's selection against
				// `spaceLinks`, so a category dropped on the way in is a category
				// DELETEd on the next save.
				const spaceReferences: ProjectReferenceItem[] = spaceLinks
					.filter((link) => !link.foreignFileId)
					.map((link) => ({
						id: link.foreignSpaceId,
						name: link.foreignSpaceName,
						provider: link.foreignSpaceProvider,
						type: link.foreignSpaceType,
						isActive: link.foreignSpaceIsActive,
					}));

				// Typed `integration` so `spaceReferenceCategory()` files them with the
				// tool connections: the chips show WHAT is linked for tools, and a
				// package connection is one space whatever half is linked.
				const toolLinkReferences: ProjectReferenceItem[] = (
					project.toolLinks ?? []
				).map((link) => ({
					id: link.spaceId,
					name: link.spaceName,
					provider: link.provider,
					type: "integration",
					isActive: link.isActive,
				}));

				return {
					...project,
					systemPrompt: project.systemPrompt ?? "",
					spaceLinks,
					fileReferences,
					spaceReferences,
					toolLinkReferences,

					createdAt: new Date(project.createdAt),
					updatedAt: new Date(project.updatedAt),
				};
			},
		},
	);

export const useDeleteProjectMutation = () =>
	$api.useMutation("delete", "/api/spaces/{spaceId}");

export const useUpdateProjectMutation = () =>
	$api.useMutation("put", "/api/projects/{projectId}");

export type BulkProjectAction =
	operations["api_projects_bulk_post"]["requestBody"]["content"]["application/json"]["action"];

export const useBulkProjectMutation = () =>
	$api.useMutation("post", "/api/projects/bulk");

export const projectTagsQueryOptions = () =>
	$api.queryOptions("get", "/api/projects/tags");

export const useCreateProjectTagMutation = () => {
	const queryClient = useQueryClient();
	return $api.useMutation("post", "/api/projects/tags", {
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["get", "/api/projects/tags"],
			});
			queryClient.invalidateQueries({ queryKey: ["get", "/api/projects"] });
		},
	});
};

export const useUpdateProjectTagMutation = () => {
	const queryClient = useQueryClient();
	return $api.useMutation("put", "/api/projects/tags/{tagId}", {
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["get", "/api/projects/tags"],
			});
			queryClient.invalidateQueries({ queryKey: ["get", "/api/projects"] });
		},
	});
};

export const useDeleteProjectTagMutation = () => {
	const queryClient = useQueryClient();
	return $api.useMutation("delete", "/api/projects/tags/{tagId}", {
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["get", "/api/projects/tags"],
			});
			queryClient.invalidateQueries({ queryKey: ["get", "/api/projects"] });
		},
	});
};

/**
 * Everything derived from a project's link set. Linking is not a local edit: the
 * chat's tool access keys on the linked spaces, and the mention palette marks
 * rows as already-linked, so a stale cache here means the model cannot reach
 * what the user just linked. Owned in one place so the two link mutations and
 * the mention-palette path cannot invalidate different subsets.
 *
 * Also driven by the `space_access_change` stream (`_authenticated/route.tsx`),
 * because a link no longer only happens under a local mutation: the
 * `create_dataset_space` tool links its new space server-side, mid-turn, and
 * nothing on this client asked for it. Without that subscription the palette
 * kept badging the new dataset "Not linked" until a reload.
 */
export const invalidateProjectLinkQueries = async (
	queryClient: ReturnType<typeof useQueryClient>,
): Promise<void> => {
	await Promise.all(
		[
			["get", "/api/projects/{projectId}"],
			["get", "/api/projects"],
			["get", "/api/references/spaces"],
			["get", "/api/references/files"],
			["accessibleSpaces"],
			["accessibleFiles"],
			["accessibleConnections"],
		].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
	);
};

/**
 * Link one space or file into a project. Replaces the `spaceLinks` array on
 * `PUT /api/projects/{projectId}`, which replaced the whole set and so made one
 * member's save drop links another had just added.
 */
export const useAddProjectLinkMutation = () => {
	const queryClient = useQueryClient();
	return $api.useMutation("post", "/api/projects/{projectId}/links", {
		onSuccess: () => invalidateProjectLinkQueries(queryClient),
	});
};

/** Unlink one space or file from a project. */
export const useRemoveProjectLinkMutation = () => {
	const queryClient = useQueryClient();
	return $api.useMutation("delete", "/api/projects/{projectId}/links", {
		onSuccess: () => invalidateProjectLinkQueries(queryClient),
	});
};

/**
 * Link a connector-package connection's tools into a project — for the caller
 * alone; co-members never get them. The connection's corpus is linked
 * separately, through {@link useAddProjectLinkMutation}.
 */
export const useAddProjectToolLinkMutation = () => {
	const queryClient = useQueryClient();
	return $api.useMutation("post", "/api/projects/{projectId}/tool-links", {
		onSuccess: () => invalidateProjectLinkQueries(queryClient),
	});
};

/** Unlink the caller's own tool link from a project. */
export const useRemoveProjectToolLinkMutation = () => {
	const queryClient = useQueryClient();
	return $api.useMutation("delete", "/api/projects/{projectId}/tool-links", {
		onSuccess: () => invalidateProjectLinkQueries(queryClient),
	});
};

// --- Project templates (FR-024) ---

export const projectTemplatesQueryOptions = () =>
	$api.queryOptions("get", "/api/projects/templates");

export const useSaveAsTemplateMutation = () => {
	const queryClient = useQueryClient();
	return $api.useMutation(
		"post",
		"/api/projects/{projectId}/save-as-template",
		{
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: ["get", "/api/projects/templates"],
				});
			},
		},
	);
};

export const useInstantiateTemplateMutation = () => {
	const queryClient = useQueryClient();
	return $api.useMutation(
		"post",
		"/api/projects/templates/{templateId}/instantiate",
		{
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ["get", "/api/projects"] });
			},
		},
	);
};

export const useDeleteTemplateMutation = () => {
	const queryClient = useQueryClient();
	return $api.useMutation("delete", "/api/projects/templates/{templateId}", {
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["get", "/api/projects/templates"],
			});
		},
	});
};
